import { ApolloError } from '@apollo/client';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import {
  renderErrorState,
  renderLoadingState,
  renderEmptyState,
  renderNoDataState,
} from './loaded-content.utils';

export interface ContentProps<T> {
  loading: boolean;
  error?: ApolloError;
  items: T[] | undefined | null;
  renderData: (items: T[]) => JSX.Element;
  renderError?: () => JSX.Element;
  renderLoading?: () => JSX.Element;
  renderNoData?: () => JSX.Element;
  renderEmpty?: () => JSX.Element;
  afterParamName?: string;
  redirectIfEmpty?: boolean;
}

export function LoadedContent<T>(props: ContentProps<T>) {
  const {
    error,
    loading,
    items,
    renderData,
    renderError = renderErrorState,
    renderLoading = renderLoadingState,
    renderNoData = renderNoDataState,
    renderEmpty = renderEmptyState,
    afterParamName,
    redirectIfEmpty = true,
  } = props;
  const navigate = useNavigate();
  const [paginationInfo] = usePaginationParams({ afterParamName });

  const isEmpty = items?.length === 0;
  const isFirstPage = !paginationInfo.after;

  useEffect(() => {
    if (redirectIfEmpty && !loading && isEmpty && !isFirstPage) {
      return navigate(-1);
    }
  }, [redirectIfEmpty, loading, isEmpty, isFirstPage, navigate]);

  if (error) {
    return renderError();
  }

  if (loading && !items) {
    return renderLoading();
  }

  if (!items) {
    return renderNoData();
  }

  if (isEmpty) {
    return renderEmpty();
  }

  return renderData(items);
}
